import * as React from 'react';

import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

const AI_PROVIDER_PLUGIN_URLS = {
    claude: 'https://claude.ai/directory/connectors/gitbook-mcp',
    cursor: 'https://cursor.com/marketplace/gitbook',
    codex: 'https://chatgpt.com/apps/gitbook/asdk_app_6a576f075ec4819196c203b7049542be',
} as const;

export type AIProvider = keyof typeof AI_PROVIDER_PLUGIN_URLS;

export const AI_PROVIDERS = Object.keys(AI_PROVIDER_PLUGIN_URLS) as AIProvider[];

/**
 * Open the GitBook plugin/connector listing for an AI provider, so the user can install it.
 */
export function AIProviderLaunchButton(props: {
    provider: AIProvider;
    onLaunch?: (provider: AIProvider) => void;
}) {
    const { provider, onLaunch } = props;
    const { label, icon, trigger } = getAIProviderLaunchProps(provider);

    return (
        <Button
            kind="secondary"
            size="medium"
            label={label}
            tooltip={{ label: `Install the GitBook plugin for ${label}` }}
            leadingIcon={icon}
            onPress={() => {
                trigger();
                onLaunch?.(provider);
            }}
            className="flex-1"
        />
    );
}

/** Create a palette for launching GitBook in an AI provider or viewing more MCP options. */
export function createAIProviderLaunchPalette(props: { onMoreOptions: () => void }) {
    const providerCommands = AI_PROVIDERS.map((provider) => {
        const { label, icon, trigger } = getAIProviderLaunchProps(provider);

        return {
            key: `mcp.${provider}`,
            title: label,
            icon,
            trigger,
        };
    });

    return createPalette({
        groups: [
            {
                key: 'mcp',
                commands: [
                    ...providerCommands,
                    {
                        key: 'mcp.more_options',
                        title: 'More options',
                        icon: Icon.MoreHorizontal,
                        trigger: props.onMoreOptions,
                    },
                ],
            },
        ],
    });
}

/** Return the shared presentation and launch action for an AI provider's MCP connector. */
export function getAIProviderLaunchProps(provider: AIProvider) {
    const trigger = () => {
        window.open(AI_PROVIDER_PLUGIN_URLS[provider], '_blank', 'noopener,noreferrer');
    };

    switch (provider) {
        case 'claude':
            return {
                label: 'Claude Code',
                icon: Icon.Claude,
                trigger,
            };
        case 'cursor':
            return {
                label: 'Cursor',
                icon: Icon.Cursor,
                trigger,
            };
        case 'codex':
            return {
                label: 'Codex',
                icon: Icon.Codex,
                trigger,
            };
        default:
            assertNever(provider);
    }
}
